import { FC } from "react";
import {
  IRefundOutsideTravel,
  IReservationRefund
} from "../../interfaces/reservations/Cancellation.types";
import { cx } from "../../utils/classnames";
import { eur } from "../../utils/eur";
import {
  refundOutsideTravelView,
  refundReturnedNothing,
  refundStatusView,
  RefundTone
} from "../../utils/reservations/cancellationText";
import InfoTip from "./cancellation/InfoTip";

const TONE: Record<RefundTone, string> = {
  muted: "bg-gray-100 text-gray-600",
  ok: "bg-green-100 text-green-700",
  warn: "bg-orange-100/30 text-orange-900",
  error: "bg-red-100 text-red-700"
};

// One line per refund decision, newest first, with where the bank says the money is.
// The money this row moved, on each rail it moved on: cash to the card or as Gjirafa Money, plus the spent
// Gjirafa Money given back in kind.
const refundAmountText = (refund: IReservationRefund): string => {
  if (refundReturnedNothing(refund)) return "Asgjë nuk u kthye";
  const returned = refund.creditsReturned ?? 0;
  if (refund.isRefundInCredits) return `Gjirafa Money ${eur(refund.amount + returned)}`;
  const card = `Kartelë ${eur(refund.amount)}`;
  return returned > 0 ? `${card} + Gjirafa Money ${eur(returned)}` : card;
};

const outsideTravelText = (refund: IRefundOutsideTravel): string =>
  refund.additionId
    ? `Kartelë ${eur(refund.amount)} · shtesa #${refund.additionId}`
    : `Kartelë ${eur(refund.amount)}`;

const RefundStatusList: FC<{
  refunds?: IReservationRefund[];
  outsideTravel?: IRefundOutsideTravel[];
}> = ({ refunds, outsideTravel }) => {
  if (!refunds?.length && !outsideTravel?.length) return null;
  const rows = [...(refunds ?? [])].sort((a, b) => b.id - a.id);
  return (
    <section className="mt-4 rounded-lg border border-gray-200 bg-white px-4 py-3">
      <h3 className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 mb-2">
        Rimbursimet
      </h3>
      <ul className="flex flex-col gap-2 text-sm">
        {outsideTravel?.map((refund) => {
          const view = refundOutsideTravelView(refund);
          return (
            <li key={refund.processorRefundId} className="flex items-center justify-between gap-3">
              <span className="flex items-center gap-1 tabular-nums">
                {outsideTravelText(refund)}
                <InfoTip text={`Stripe ${refund.processorRefundId}`} />
              </span>
              <span className={cx(["rounded px-2 py-0.5 text-xs font-medium", TONE[view.tone]])}>
                {view.label}
              </span>
            </li>
          );
        })}
        {rows.map((refund) => {
          const view = refundStatusView(refund);
          return (
            <li key={refund.id} className="flex flex-col gap-1">
              <div className="flex items-center justify-between gap-3">
                <span className="tabular-nums">
                  #{refund.id} · {refundAmountText(refund)}
                </span>
                <span className={cx(["rounded px-2 py-0.5 text-xs font-medium", TONE[view.tone]])}>
                  {view.label}
                </span>
              </div>
              {view.note && (
                <p className="rounded-md border border-red-300 bg-red-100/50 px-3 py-2 text-xs text-red-700">
                  {view.note}
                </p>
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
};

export default RefundStatusList;
